import React, { useEffect, useRef } from "react";

import { FullPageModal } from "../../components";
import { useVisualizerContext } from "../../contexts/VisualizerContext";
import { DataVisualizer } from "./DataVisualizer";

export const VisualizerModal: React.FC = (): JSX.Element => {
  const { isVisualizerModalOpen, setIsVisualizerModalOpen, visualizerInfo } =
    useVisualizerContext();
  const historyPushedRef = useRef(false);
  const isCleaningUpRef = useRef(false);

  // Handle browser back button to close visualizer
  useEffect(() => {
    if (isVisualizerModalOpen) {
      // Push a history entry when visualizer opens
      if (!historyPushedRef.current) {
        window.history.pushState({ visualizerOpen: true }, "");
        historyPushedRef.current = true;
      }

      // Listen for popstate events (back/forward button)
      const handlePopState = () => {
        // Only close if we're not cleaning up and the modal is still open
        if (!isCleaningUpRef.current && isVisualizerModalOpen) {
          setIsVisualizerModalOpen(false);
          historyPushedRef.current = false;
        }
      };

      window.addEventListener("popstate", handlePopState);

      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    } else {
      // Clean up history entry when modal closes via X button
      if (historyPushedRef.current && !isCleaningUpRef.current) {
        isCleaningUpRef.current = true;
        historyPushedRef.current = false;
        // Remove the history entry we added
        // Check if current state is our visualizer state before going back
        const currentState = window.history.state;
        if (
          currentState &&
          typeof currentState === "object" &&
          "visualizerOpen" in currentState
        ) {
          window.history.back();
        }
        // Reset cleanup flag after a brief delay
        setTimeout(() => {
          isCleaningUpRef.current = false;
        }, 100);
      }
    }
  }, [isVisualizerModalOpen, setIsVisualizerModalOpen]);

  const handleClose = () => {
    setIsVisualizerModalOpen(false);
  };

  return (
    <FullPageModal
      isOpen={isVisualizerModalOpen}
      onClose={handleClose}
      title="Visualize"
      component="visualizer"
    >
      <div className="w-full h-full">
        <DataVisualizer sessionInfo={visualizerInfo} />
      </div>
    </FullPageModal>
  );
};
